/**
 * NavigationProvider Component
 * 
 * Centralizes all navigation logic and state management.
 * Provides a unified interface for navigation throughout the app.
 * 
 * Features:
 * - Language-aware navigation
 * - Story and episode navigation with slug support
 * - Navigation history tracking
 * - Breadcrumb generation
 * - Current location context
 */

import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storyIndexService } from '@/services/storyIndexService';
import type { Language } from '@/config';

/**
 * Navigation context value interface
 */
export interface NavigationContextValue {
  /** Current language */
  currentLanguage: Language;
  
  /** Current story ID (if on story/episode page) */
  currentStoryId: string | null;
  
  /** Current episode ID (if on episode page) */
  currentEpisodeId: string | null;
  
  /** Navigate to home page */
  goHome: () => void;
  
  /** Navigate to a specific story */
  goToStory: (storyId: string) => void;
  
  /** Navigate to a specific episode using episode ID */
  goToEpisode: (storyId: string, episodeId: string, episodeSlug?: string) => void;
  
  /** Navigate to a specific episode using episode slug */
  goToEpisodeBySlug: (storyId: string, episodeSlug: string) => void;
  
  /** Go back to previous page */
  goBack: () => void;
  
  /** Navigate with language prefix */
  navigateWithLanguage: (path: string) => void;
  
  /** Change language and update URL */
  changeLanguage: (newLanguage: Language) => void;
  
  /** Get breadcrumb trail for current location */
  getBreadcrumbs: () => Breadcrumb[];
  
  /** Check if currently on a specific page type */
  isOnPage: (pageType: PageType) => boolean;
}

/**
 * Breadcrumb item
 */
export interface Breadcrumb {
  label: string;
  path: string;
  isActive: boolean;
}

/**
 * Page types for navigation context
 */
export type PageType = 'home' | 'story' | 'episode' | 'notFound';

/**
 * Navigation context
 */
const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

/**
 * NavigationProvider props
 */
export interface NavigationProviderProps {
  children: React.ReactNode;
}

/**
 * NavigationProvider Component
 * 
 * Wraps the application and provides navigation context.
 */
export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { i18n } = useTranslation();
  
  // State
  const [currentStoryId, setCurrentStoryId] = useState<string | null>(null);
  const [currentEpisodeId, setCurrentEpisodeId] = useState<string | null>(null);
  
  // Derive current language from i18n
  const currentLanguage = (i18n.language.split('-')[0] as Language) || 'en';
  
  /**
   * Sync i18n language with URL language parameter
   */
  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const urlLanguage = pathParts[0];
    
    // Check if first path segment is a valid language
    if (urlLanguage === 'en' || urlLanguage === 'fr') {
      const currentLang = i18n.language.split('-')[0];
      
      // Update i18n if URL language differs from current language
      if (currentLang !== urlLanguage) {
        console.log('[NavigationProvider] Syncing language from URL:', urlLanguage);
        i18n.changeLanguage(urlLanguage);
      }
    }
  }, [location.pathname, i18n]);
  
  /**
   * Redirect from root to language-specific home
   */
  useEffect(() => {
    if (location.pathname === '/') {
      // Detect browser language or use default
      const browserLang = navigator.language.split('-')[0];
      const targetLang: Language = (browserLang === 'fr' || browserLang === 'en') ? browserLang : 'en';
      
      // Update i18n language if needed
      if (i18n.language !== targetLang) {
        i18n.changeLanguage(targetLang);
      }
      
      // Redirect to language-specific home
      navigate(`/${targetLang}`, { replace: true });
    }
  }, [location.pathname, navigate, i18n]);
  
  /**
   * Update current context based on URL params
   */
  useEffect(() => {
    const updateContext = async () => {
      const { storySlug, storyId, episodeSlug, episodeId } = params as any;
      
      // Load index if needed
      await storyIndexService.loadIndex();
      
      // Resolve story ID from slug or use direct ID
      if (storySlug) {
        const resolvedId = storyIndexService.getStoryIdBySlug(storySlug, currentLanguage);
        setCurrentStoryId(resolvedId || null);
      } else if (storyId) {
        setCurrentStoryId(storyId);
      } else {
        setCurrentStoryId(null);
      }
      
      // Set episode ID
      if (episodeSlug || episodeId) {
        setCurrentEpisodeId(episodeSlug || episodeId || null);
      } else {
        setCurrentEpisodeId(null);
      }
    };
    
    updateContext();
  }, [location.pathname, params, currentLanguage]);
  
  /**
   * Build a language-aware route path
   */
  const buildRoutePath = useCallback((language: Language, path: string = ''): string => {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `/${language}${cleanPath ? `/${cleanPath}` : ''}`;
  }, []);
  
  /**
   * Get story slug from story ID
   */
  const getStorySlug = useCallback((storyId: string): string => {
    const config = storyIndexService.getStoryConfig(storyId);
    return config?.folders[currentLanguage] || storyId;
  }, [currentLanguage]);
  
  /**
   * Navigate to home page
   */
  const goHome = useCallback(() => {
    navigate(buildRoutePath(currentLanguage));
  }, [navigate, buildRoutePath, currentLanguage]);
  
  /**
   * Navigate to a specific story
   */
  const goToStory = useCallback((storyId: string) => {
    const storySlug = getStorySlug(storyId);
    const path = buildRoutePath(currentLanguage, storySlug);
    navigate(path);
  }, [navigate, buildRoutePath, currentLanguage, getStorySlug]);
  
  /**
   * Navigate to a specific episode
   */
  const goToEpisode = useCallback((storyId: string, episodeId: string, episodeSlug?: string) => {
    const storySlug = getStorySlug(storyId);
    const slug = episodeSlug || episodeId;
    
    console.log('[NavigationProvider] goToEpisode:', {
      storyId,
      episodeId,
      episodeSlug,
      storySlug,
      finalSlug: slug,
      currentLanguage
    });
    
    const path = buildRoutePath(currentLanguage, `${storySlug}/${slug}`);
    console.log('[NavigationProvider] Navigating to:', path);
    navigate(path);
  }, [navigate, buildRoutePath, currentLanguage, getStorySlug]);
  
  /**
   * Navigate to a specific episode using slug
   */
  const goToEpisodeBySlug = useCallback((storyId: string, episodeSlug: string) => {
    goToEpisode(storyId, episodeSlug, episodeSlug);
  }, [goToEpisode]);
  
  /**
   * Go back to previous page
   */
  const goBack = useCallback(() => {
    window.history.back();
  }, []);
  
  /**
   * Navigate with language prefix
   */
  const navigateWithLanguage = useCallback((path: string) => {
    const fullPath = buildRoutePath(currentLanguage, path);
    navigate(fullPath);
  }, [navigate, buildRoutePath, currentLanguage]);
  
  /**
   * Change language and update URL
   */
  const changeLanguage = useCallback((newLanguage: Language) => {
    // Update i18n language
    i18n.changeLanguage(newLanguage);
    
    // Reconstruct current path with new language
    const pathParts = location.pathname.split('/').filter(Boolean);
    
    if (pathParts.length > 0 && (pathParts[0] === 'en' || pathParts[0] === 'fr')) {
      // Replace language in path
      pathParts[0] = newLanguage;
      navigate(`/${pathParts.join('/')}`);
    } else {
      // Add language prefix
      navigate(buildRoutePath(newLanguage, location.pathname));
    }
  }, [i18n, location.pathname, navigate, buildRoutePath]);
  
  /**
   * Get breadcrumb trail for current location
   */
  const getBreadcrumbs = useCallback((): Breadcrumb[] => {
    const breadcrumbs: Breadcrumb[] = [];
    
    // Always add home
    breadcrumbs.push({
      label: 'Home',
      path: buildRoutePath(currentLanguage),
      isActive: !currentStoryId && !currentEpisodeId,
    });
    
    // Add story if present
    if (currentStoryId) {
      const storyConfig = storyIndexService.getStoryConfig(currentStoryId);
      const storySlug = getStorySlug(currentStoryId);
      
      breadcrumbs.push({
        label: storyConfig?.titles[currentLanguage] || currentStoryId,
        path: buildRoutePath(currentLanguage, storySlug),
        isActive: !currentEpisodeId,
      });
    }
    
    // Add episode if present
    if (currentEpisodeId) {
      breadcrumbs.push({
        label: `Episode ${currentEpisodeId.replace('episode_', '')}`,
        path: location.pathname,
        isActive: true,
      });
    }
    
    return breadcrumbs;
  }, [currentLanguage, currentStoryId, currentEpisodeId, location.pathname, buildRoutePath, getStorySlug]);
  
  /**
   * Check if currently on a specific page type
   */
  const isOnPage = useCallback((pageType: PageType): boolean => {
    switch (pageType) {
      case 'home':
        return !currentStoryId && !currentEpisodeId;
      case 'story':
        return !!currentStoryId && !currentEpisodeId;
      case 'episode':
        return !!currentEpisodeId;
      case 'notFound':
        return location.pathname.includes('*') || location.pathname.includes('404');
      default:
        return false;
    }
  }, [currentStoryId, currentEpisodeId, location.pathname]);
  
  /**
   * Memoized context value
   */
  const contextValue = useMemo<NavigationContextValue>(
    () => ({
      currentLanguage,
      currentStoryId,
      currentEpisodeId,
      goHome,
      goToStory,
      goToEpisode,
      goToEpisodeBySlug,
      goBack,
      navigateWithLanguage,
      changeLanguage,
      getBreadcrumbs,
      isOnPage,
    }),
    [
      currentLanguage,
      currentStoryId,
      currentEpisodeId,
      goHome,
      goToStory,
      goToEpisode,
      goToEpisodeBySlug,
      goBack,
      navigateWithLanguage,
      changeLanguage,
      getBreadcrumbs,
      isOnPage,
    ]
  );
  
  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Hook to use navigation context
 * 
 * @throws Error if used outside NavigationProvider
 */
export const useNavigation = (): NavigationContextValue => {
  const context = useContext(NavigationContext);
  
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  
  return context;
};

/**
 * Export context for advanced use cases
 */
export { NavigationContext };
